import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'src/presentation/components/agent_desktop_components';
import EmptyPageLayout from 'src/presentation/components/pages/shared/EmptyPageLayout';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import NotActivatedThreadsBackgroundSvg from 'src/images/NotActivatedThreadsBackground.svg';
import NotActivatedThreadsSvg from 'src/images/NotActivatedThreads.svg';
import { RootState } from 'src/actions/configuration/rootReducer';
import threadsConfigurationResource, { ThreadsConfig } from 'src/actions/threads/threadsConfigurationResource';

const ActiveThreadsPage = () => {
  const dispatch = useDispatch();
  const agentId = useSelector((state: RootState) => state.auth.userId);

  const feesDocs = (
    <ExternalLink
      label="fees"
      to="https://help.gladly.com/implementation/docs/invoice-and-billing-overview#payperuse-invoices"
    />
  );

  const threadsDocs = (
    <ExternalLink label="Self-Service Threads" to="https://help.gladly.com/docs/what-is-self-service-threads" />
  );

  const handleActivate = () => {
    const threadsConfiguration: ThreadsConfig = {
      enabled: true,
      enabledOn: new Date().toISOString(),
      enabledBy: agentId!,
    };
    dispatch(
      threadsConfigurationResource.effects.replace({}, threadsConfiguration, {
        successToastMessage: 'Self-Service Threads has been activated.',
        errorToastMessage: 'Unable to activate Self-Service Threads. Please try again.',
      })
    );
  };

  const activateButton = <Button onClick={handleActivate}>Activate</Button>;

  return (
    <EmptyPageLayout data-aid="threads-are-not-activated">
      <Body>
        <FirstColumn>
          <StyledH1>Help more customers help themselves with Self-Service Threads</StyledH1>
          <StyledP>
            Give customers the fast, convenient experience to solve their inquiries, unlock the rich customer data in
            Gladly, and equip agents with context when customers need assistance.
          </StyledP>
          <StyledP>
            By clicking “Activate” you are agreeing to the {feesDocs} associated with
            <br /> {threadsDocs}.
          </StyledP>
          {activateButton}
        </FirstColumn>
        <SecondColumn>
          <ImageContainer>
            <BackgroundImage src={NotActivatedThreadsBackgroundSvg} />
            <StyledImage src={NotActivatedThreadsSvg} />
          </ImageContainer>
        </SecondColumn>
      </Body>
    </EmptyPageLayout>
  );
};

const StyledH1 = styled(H1)`
  line-height: 29px;
`;

const StyledP = styled.p`
  margin-bottom: 24px;
`;

const FirstColumn = styled.div`
  padding: 0 ${p => p.theme.spacing.insetLarge};
`;
const SecondColumn = styled.div``;

const Body = styled.div`
  display: grid;
  grid-template-columns: 510px 1fr;
  column-gap: 60px;

  @media (max-width: 1180px) {
    grid-template-columns: 100%;

    ${SecondColumn} {
      display: none;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
`;

const BackgroundImage = styled.img`
  position: relative;
  left: -12px;
  max-width: 260px;
  margin-bottom: 20px;
`;

const StyledImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  max-width: 260px;
  margin-bottom: 20px;
`;

export default ActiveThreadsPage;
